import React, { useContext, useRef } from 'react';
import styled from 'styled-components';
import { VSpace } from '@/components/spacing';
import { JsCodeEditor } from '@/features/editor/widgets/code-editors/monaco-editor';
import { Tooltip } from '@/components/tooltip';
import { centered, useSharedElement } from '@/components/use-shared-element';
import { SharedElementOverlay } from '@/components/shared-element-overlay';
import { FullScreenButton } from '@/features/editor/widgets/track-element/FullScreenButton';
import { TrackElementContext } from '@/features/editor/widgets/track-element/context';

const defaultCode = `// EXAMPLE CODE – This is just a sample implementation. You can change the logic,
// but make sure to call: trackCustomEvent();
// as it's required to report the metric to our backend.
// try pasting the code in browser console to see the result.
const observer = new MutationObserver(() => {
  document.querySelectorAll("a.button.button--primary").forEach((button) => {
    if (!button.dataset.vslyListenerAttached) {
      button.addEventListener("click", () => {
        trackCustomEvent(); // <- magic method that reports the metric to our backend.
      });
      button.dataset.vslyListenerAttached = "true";
    }
  });
});
observer.observe(document.body, { childList: true, subtree: true });`;

export function CodeBlock() {
  const { code, setCode, setJsErrors } = useContext(TrackElementContext);

  const sharedElement = useModalSharedElement();
  const ref = useRef();
  return (
    <Wrapper textArea ref={ref}>
      <SharedElementOverlay {...sharedElement.props} fromElement={ref}>
        <JsCodeEditor
          code={code || defaultCode}
          onChange={setCode}
          readOnly={false}
        />
      </SharedElementOverlay>
      <Row>
        <Flex>
          <Label>JS code that triggers the event</Label>
          <CodeTooltip />
        </Flex>
        <FullScreenButton sharedElement={sharedElement} />
      </Row>
      <VSpace />
      <CodeEditorMonacoWrapper>
        <JsCodeEditor
          setJsErrors={setJsErrors}
          code={code || defaultCode}
          onChange={setCode}
          readOnly={false}
        />
      </CodeEditorMonacoWrapper>
      <VSpace />
    </Wrapper>
  );
}
const Flex = styled.div`
  display: flex;
`;
const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;
const Label = styled.span`
  user-select: none;
  color: #5b656e;
  font-size: 1.4rem;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: start;
  align-items: start;
  color: black;
  padding: 2rem;

  font-family: Inter, serif;
  text-align: center;

  input {
    justify-self: center;
    box-shadow: none;
    font-size: 1.4rem;
    font-family: 'JetBrains Mono', serif;
    border-color: #dedede;
  }
`;

export const CodeEditorMonacoWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 400px;
  border: 1px solid #d4dfe4;
  border-radius: 1rem;
  padding: 1rem;
  background: #f6f8f9;
`;

function CodeTooltip() {
  return (
    <Tooltip
      positions={[`bottom`]}
      zIndex={2}
      title="Custom Event "
      text={
        <span>
          • Wait for elements to appear: Some elements may load asynchronously,
          so consider using a MutationObserver or setInterval to detect them
          reliably. <br />• Avoid duplicate event listeners: Make sure you only
          attach event listeners once per element to prevent duplicate metric
          reporting.
          <br />• Make css selectors/regex are valid
        </span>
      }
    />
  );
}

const useModalSharedElement = () =>
  useSharedElement(
    {
      showBackdrop: true,
      extraFrom: {
        background: `#dedede`,
        opacity: `0`,
      },
      extraTo: {
        background: `white`,
        opacity: `1`,
      },
    },
    undefined,
    () => centered(75, 115),
  );
